// Centralized FE service instances — single instantiation point.
// Components import service instances from here, call methods directly.
// No wrapper functions — consumers use try/catch for error handling.

import { supabaseClient } from "../utility/supabaseClient";
import { FakturowniaEdgeFnClient } from "@shared/services/edgeFn/FakturowniaEdgeFnClient";
import { FakturowniaPaymentResolutionService } from "@shared/services/fakturownia/payments/FakturowniaPaymentResolutionService";
import { BankEdgeFnClient } from "@shared/services/edgeFn/BankEdgeFnClient";
import { CompanyLookupEdgeFnClient } from "@shared/services/edgeFn/CompanyLookupEdgeFnClient";
import { CompanyGeocodingEdgeFnClient } from "@shared/services/edgeFn/CompanyGeocodingEdgeFnClient";
import { EmailService } from "@shared/services/email/EmailService";
import { InvoiceDao } from "@shared/services/invoice/InvoiceDao";
import { InvoiceAccountAssignService } from "@shared/services/invoice/InvoiceAccountAssignService";
import { InvoiceAttachmentDao } from "@shared/services/invoice/InvoiceAttachmentDao";
import { InvoiceService } from "@shared/services/invoice/InvoiceService";
import { InvoiceReconcileService } from "@shared/services/invoice/InvoiceReconcileService";
import { InvoiceApprovalService } from "@shared/services/invoice/InvoiceApprovalService";
import { InvoiceCorrectionRequestService } from "@shared/services/invoice/InvoiceCorrectionRequestService";
import { InvoiceGenerationService } from "@shared/services/invoice/generation/InvoiceGenerationService";
import { CompanyService } from "@shared/services/company/CompanyService";
import { TransactionService } from "@shared/services/transaction/TransactionService";
import { TransactionDao } from "@shared/services/transaction/TransactionDao";
import { EmailSendDao } from "@shared/services/email/EmailSendDao";
import { ProductDao } from "@shared/services/product/ProductDao";
import { TransactionFileDao } from "@shared/services/file/TransactionFileDao";
import { TransactionFileService } from "@shared/services/file/TransactionFileService";
import { EdprEdgeFnClient } from "@shared/services/edgeFn/EdprEdgeFnClient";
import { PipelineEdgeFnClient } from "@shared/services/edgeFn/PipelineEdgeFnClient";
import { TranseuEdgeFnClient } from "@shared/services/edgeFn/TranseuEdgeFnClient";
import { EdprDocumentSyncService } from "@shared/services/edpr/EdprDocumentSyncService";
import { EdprRequestService } from "@shared/services/edpr/EdprRequestService";
import { EdprDocumentDao } from "@shared/services/edpr/EdprDocumentDao";
import { EdprRequestDao } from "@shared/services/edpr/EdprRequestDao";
import { CommissionReportDao } from "@shared/services/commission/CommissionReportDao";
import { CommissionAdjustmentDao } from "@shared/services/commission/CommissionAdjustmentDao";
import { CommissionRuleDao } from "@shared/services/commission/CommissionRuleDao";
import { CommissionPayeeDao } from "@shared/services/commission/CommissionPayeeDao";
import { AppConfigDao } from "@shared/services/settings/AppConfigDao";
import { PriceListDao } from "@shared/services/price_list/PriceListDao";
import { MbankPaymentLinkDao } from "@shared/services/mbank/dao/MbankPaymentLinkDao";
import { MbankStatementLineDao } from "@shared/services/mbank/dao/MbankStatementLineDao";
import { CompanyBankAccountDao } from "@shared/services/mbank/dao/CompanyBankAccountDao";
import { FakturowniaPaymentDao } from "@shared/services/fakturownia/payments/FakturowniaPaymentDao";
import { ShadowDao } from "@shared/services/fakturownia/shadow/ShadowDao";
import { DashboardSectionDao } from "@shared/services/dashboard/DashboardSectionDao";
import { MobileUploadTokenDao } from "@shared/services/mobile/MobileUploadTokenDao";
import { UploadTokenDao } from "@shared/services/mobile/UploadTokenDao";
import { UserGoogleTokenDao } from "@shared/services/google/UserGoogleTokenDao";
import { GlobalSearchDao } from "@shared/services/search/GlobalSearchDao";
import { LogisticsDocumentReportDao } from "@shared/services/document/LogisticsDocumentReportDao";
import { AwizacjaImportService } from "@shared/services/awizacja/AwizacjaImportService";
import { googleClient } from "./google/withGoogleAuth";

// ── Google OAuth wrapper ────────────────────────────────────────────────
// Moved to a dedicated module so consumers (forms in src/entities/) can
// import from the lighter path and keep their tests fast.
export { googleClient, withGoogleAuth, callGoogleMethod } from "./google/withGoogleAuth";

// ── Service instances ───────────────────────────────────────────────────

export const invoiceDao = new InvoiceDao(supabaseClient);
export const transactionSvc = new TransactionService(supabaseClient);
export const transactionDao = new TransactionDao(supabaseClient);
export const emailSendDao = new EmailSendDao(supabaseClient);
export const productDao = new ProductDao(supabaseClient);
export const companySvc = new CompanyService(supabaseClient);

export const emailService = new EmailService(supabaseClient, googleClient, async () => {
  const { data: { user } } = await supabaseClient.auth.getUser();
  return { id: user?.id ?? '', email: user?.email ?? undefined };
});
export const invoiceSvc = new InvoiceService(supabaseClient);
export const invoiceGenerationSvc = new InvoiceGenerationService(supabaseClient);
export const reconcileSvc = new InvoiceReconcileService(supabaseClient, undefined, companySvc);
export const approvalSvc = new InvoiceApprovalService(invoiceDao);
export const fakturowniaSvc = new FakturowniaEdgeFnClient(supabaseClient);
export const bankSvc = new BankEdgeFnClient(supabaseClient);
export const companyLookupSvc = new CompanyLookupEdgeFnClient(supabaseClient);
export const companyGeocodingSvc = new CompanyGeocodingEdgeFnClient(supabaseClient);
export const fileDao = new TransactionFileDao(supabaseClient);
export const fileSvc = new TransactionFileService(fileDao, googleClient);
export const edprEdgeFn = new EdprEdgeFnClient(supabaseClient);
export const pipelineEdgeFn = new PipelineEdgeFnClient(supabaseClient);
export const transeuSvc = new TranseuEdgeFnClient(supabaseClient);
export const edprSyncSvc = new EdprDocumentSyncService(supabaseClient);
export const edprReqSvc = new EdprRequestService(supabaseClient);
export const edprDocumentDao = new EdprDocumentDao(supabaseClient);
export const edprRequestDao = new EdprRequestDao(supabaseClient);
export const commissionReportDao = new CommissionReportDao(supabaseClient);
export const commissionAdjustmentDao = new CommissionAdjustmentDao(supabaseClient);
export const commissionRuleDao = new CommissionRuleDao(supabaseClient);
export const commissionPayeeDao = new CommissionPayeeDao(supabaseClient);
export const appConfigDao = new AppConfigDao(supabaseClient);
export const priceListDao = new PriceListDao(supabaseClient);
export const mbankPaymentLinkDao = new MbankPaymentLinkDao(supabaseClient);
export const mbankStatementLineDao = new MbankStatementLineDao(supabaseClient);
export const fakturowniaPaymentDao = new FakturowniaPaymentDao(supabaseClient);
export const dashboardSectionDao = new DashboardSectionDao(supabaseClient);
export const mobileUploadTokenDao = new MobileUploadTokenDao(supabaseClient);
export const uploadTokenDao = new UploadTokenDao(supabaseClient);
export const userGoogleTokenDao = new UserGoogleTokenDao(supabaseClient);
export const globalSearchDao = new GlobalSearchDao(supabaseClient);
export const logisticsDocumentReportDao = new LogisticsDocumentReportDao(supabaseClient);
export const awizacjaImportSvc = new AwizacjaImportService(supabaseClient);
export const invoiceAttachmentDao = new InvoiceAttachmentDao(supabaseClient);
export const invoiceCorrectionRequestSvc = new InvoiceCorrectionRequestService(googleClient, invoiceDao, emailService, transactionDao);

// ── Interim (payments_invoice_linking=false) pairing resolutions ──────────
// Invoked DIRECTLY from the browser via the shared
// FakturowniaPaymentResolutionService + DAO (owner decision 2026-07-31;
// previously fakturownia-sync edge-fn actions). RLS: employees hold a
// column-scoped UPDATE grant on fakturownia_payment's pairing columns
// (migration 20260731120000_fa_payment_pairing_browser_writes).
export const companyBankAccountDao = new CompanyBankAccountDao(supabaseClient);
export const fakturowniaPaymentResolutionSvc = new FakturowniaPaymentResolutionService(
  fakturowniaPaymentDao,
  mbankPaymentLinkDao,
  invoiceDao,
  mbankStatementLineDao,
  companyBankAccountDao,
);

// ── Invoice bank-account correction rules (R2) ─────────────────────────────
// Invoked DIRECTLY from the browser — an operator assigns an already-verified
// registry account onto a received invoice missing its seller_bank_account.
const shadowDao = new ShadowDao(supabaseClient);
export const invoiceAccountAssignSvc = new InvoiceAccountAssignService(
  invoiceDao,
  companyBankAccountDao,
  shadowDao,
);

async function currentUserEmail(): Promise<string> {
  const { data: { user } } = await supabaseClient.auth.getUser();
  return user?.email ?? "unknown";
}

export async function pairingAcceptManual(externalPaymentId: string): Promise<void> {
  await fakturowniaPaymentResolutionSvc.acceptManualException(
    externalPaymentId,
    await currentUserEmail(),
  );
}

export async function pairingLinkLine(
  externalPaymentId: string,
  statementLineId: number,
): Promise<void> {
  await fakturowniaPaymentResolutionSvc.pairToLine(
    externalPaymentId,
    statementLineId,
    await currentUserEmail(),
  );
}

export async function pairingUnpair(externalPaymentId: string): Promise<void> {
  await fakturowniaPaymentResolutionSvc.unpair(externalPaymentId);
}
